import { Controller } from "stimulus"

/**
 * MultiselectDropdownController
 * =============================
 * Generic multi-select dropdown that allows selecting multiple options.
 *
 * WHAT IT DOES:
 * - Creates a dropdown where users can select multiple options
 * - Shows selected options as comma-separated text in the display area
 * - Stores selected values in a hidden input for form submission
 * - Closes automatically when clicking outside the dropdown
 *
 * BEHAVIOR:
 * - When user clicks the dropdown trigger: The options list toggles open/closed.
 *   The element gets an "is-open" class for styling (e.g., rotating arrow icon).
 * - When user clicks an option: The option is toggled (selected/deselected).
 *   Selected options get an "is-selected" class. The display text updates to
 *   show all selected option labels. The hidden input value updates.
 * - When user clicks outside: The dropdown closes automatically.
 * - When clear() is called: All selections are removed.
 *
 * HTML USAGE:
 *   <div data-controller="multiselect-dropdown"
 *        data-multiselect-dropdown-placeholder-value="Select options...">
 *
 *     <!-- Clickable trigger area -->
 *     <div data-action="click->multiselect-dropdown#toggle">
 *       <span data-multiselect-dropdown-target="display">Select options...</span>
 *       <i class="dropdown-arrow"></i>
 *     </div>
 *
 *     <!-- Options list (hidden by default, shown when open) -->
 *     <ul data-multiselect-dropdown-target="list">
 *       <li data-multiselect-dropdown-target="option"
 *           data-value="option1"
 *           data-action="click->multiselect-dropdown#selectOption">
 *         Option 1
 *         <span class="checkmark"></span>
 *       </li>
 *       <li data-multiselect-dropdown-target="option"
 *           data-value="option2"
 *           data-action="click->multiselect-dropdown#selectOption">
 *         Option 2
 *         <span class="checkmark"></span>
 *       </li>
 *     </ul>
 *
 *     <!-- Hidden input for form submission -->
 *     <input type="hidden"
 *            name="selected_options"
 *            data-multiselect-dropdown-target="hiddenInput">
 *   </div>
 *
 * TARGETS:
 *   - display: Element showing the current selection text (or placeholder)
 *   - list: The <ul> or container holding all options (toggled visible/hidden)
 *   - option: Each selectable option (should have data-value attribute)
 *   - hiddenInput: Hidden input that stores comma-separated selected values
 *
 * VALUES (all optional):
 *   - placeholder: Text shown when nothing selected (default: "Select options")
 *   - openClass: Class added to element when open (default: "is-open")
 *   - visibleClass: Class added to list when visible (default: "visible")
 *   - selectedClass: Class added to selected options (default: "is-selected")
 *   - placeholderClass: Class on display when showing placeholder (default: "is-placeholder")
 *   - separator: Character to join values in hidden input (default: ",")
 *   - errorClass: Class for error state (default: "has-error")
 *   - errorDuration: How long to show error state in ms (default: 3000)
 *
 * ACTIONS (usable from HTML):
 *   - toggle: Opens/closes the dropdown
 *   - selectOption: Toggles selection of an option
 *   - clear: Clears all selections
 *   - showError: Shows error state temporarily
 *
 */
export default class extends Controller {
  static targets = ["list", "option", "display", "hiddenInput"]
  static values = {
    placeholder: String,
    openClass: String,
    visibleClass: String,
    selectedClass: String,
    placeholderClass: String,
    separator: String,
    errorClass: String,
    errorDuration: Number
  }

  get placeholder() {
    return this.hasPlaceholderValue ? this.placeholderValue : "Select options"
  }

  get openClass() {
    return this.hasOpenClassValue ? this.openClassValue : "is-open"
  }

  get visibleClass() {
    return this.hasVisibleClassValue ? this.visibleClassValue : "visible"
  }

  get selectedClass() {
    return this.hasSelectedClassValue ? this.selectedClassValue : "is-selected"
  }

  get placeholderClass() {
    return this.hasPlaceholderClassValue ? this.placeholderClassValue : "is-placeholder"
  }

  get separator() {
    return this.hasSeparatorValue ? this.separatorValue : ","
  }

  get errorClass() {
    return this.hasErrorClassValue ? this.errorClassValue : "has-error"
  }

  get errorDuration() {
    return this.hasErrorDurationValue ? this.errorDurationValue : 3000
  }

  // Called when controller connects to the DOM.
  // Sets up initial state and click-outside listener.
  connect() {
    this.selectedValues = []
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    window.addEventListener("click", this.boundCloseOnClickOutside)
    this.updateDisplay()
  }

  // Called when controller disconnects from the DOM.
  // Cleans up the click-outside listener.
  disconnect() {
    window.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  // Toggles the dropdown open/closed.
  // Triggered by: data-action="click->multiselect-dropdown#toggle"
  toggle(event) {
    // If clicking inside the list, don't toggle (let selectOption handle it)
    if (this.hasListTarget && this.listTarget.contains(event.target)) {
      return
    }
    event.stopPropagation()
    if (this.hasListTarget) {
      this.listTarget.classList.toggle(this.visibleClass)
    }
    this.element.classList.toggle(this.openClass)
  }

  // Selects or deselects an option.
  // Triggered by: data-action="click->multiselect-dropdown#selectOption" on each option
  selectOption(event) {
    event.stopPropagation()
    const option = event.currentTarget
    const value = option.dataset.value

    // Toggle selection
    if (this.selectedValues.includes(value)) {
      // Deselect: remove from array and remove selected class
      this.selectedValues = this.selectedValues.filter(v => v !== value)
      option.classList.remove(this.selectedClass)
    } else {
      // Select: add to array and add selected class
      this.selectedValues.push(value)
      option.classList.add(this.selectedClass)
    }

    this.updateDisplay()
    this.updateHiddenInput()
  }

  // Updates the display text to show selected options or placeholder.
  updateDisplay() {
    if (!this.hasDisplayTarget) return

    if (this.selectedValues.length === 0) {
      // No selection: show placeholder
      this.displayTarget.textContent = this.placeholder
      this.displayTarget.classList.add(this.placeholderClass)
    } else {
      // Has selection: show comma-separated labels
      const selectedLabels = this.selectedValues.map(value => {
        const option = this.optionTargets.find(opt => opt.dataset.value === value)
        return option ? option.textContent.trim() : value
      })
      this.displayTarget.textContent = selectedLabels.join(", ")
      this.displayTarget.classList.remove(this.placeholderClass)
    }
  }

  // Updates the hidden input value for form submission.
  updateHiddenInput() {
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = this.selectedValues.join(this.separator)
    }
  }

  // Closes dropdown when clicking outside of it.
  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  // Closes the dropdown.
  close() {
    if (this.hasListTarget) {
      this.listTarget.classList.remove(this.visibleClass)
    }
    this.element.classList.remove(this.openClass)
  }

  // Clears all selections.
  // Triggered by: data-action="...->multiselect-dropdown#clear"
  clear() {
    this.selectedValues = []
    this.optionTargets.forEach(option => option.classList.remove(this.selectedClass))
    this.updateDisplay()
    this.updateHiddenInput()
  }

  // Shows error state temporarily (adds error class, removes after duration).
  // Triggered by: data-action="...->multiselect-dropdown#showError"
  showError() {
    this.element.classList.add(this.errorClass)
    setTimeout(() => {
      this.element.classList.remove(this.errorClass)
    }, this.errorDuration)
  }
}
